import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { BasicInfoApiService } from '@core/http/api/basic-info.service';
import { BehaviorSubject } from 'rxjs';
import { SKIP_LOGIN_REDIRECT_HEADER } from 'src/app/xiaohong-interceptor.service';
import { environment } from 'src/environments/environment';

type SpotlightEnvironment = {
  demo: boolean;
  auto_login?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class StatesService {
  private basicInfoApi = inject(BasicInfoApiService);
  private document = inject(DOCUMENT);

  // 用户信息
  public userInfo$: BehaviorSubject<UserInfo> = new BehaviorSubject({} as UserInfo);
  public accountInfo$: BehaviorSubject<Create.CreateCampaignAccount[]> =
    new BehaviorSubject([] as Create.CreateCampaignAccount[]);
  public saasUserInfo$: BehaviorSubject<any> = new BehaviorSubject({});
  public environmentInfo$: BehaviorSubject<SpotlightEnvironment> =
    new BehaviorSubject<SpotlightEnvironment>({ demo: false });
  public demoLoginRequired$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  /**
   * 启动鉴权：先获取环境，再获取用户信息。
   */
  public async authorizationInit(): Promise<boolean> {
    const environmentResponse = await this.basicInfoApi.getEnvironment({}).toPromise();
    const environmentInfo = environmentResponse?.data || { demo: false };
    this.environmentInfo$.next(environmentInfo);

    const userInfo = await this.userInfoInit(environmentInfo.demo === true);
    const loginRequired =
      environmentInfo.demo === true && String(userInfo?.id) !== '999999';
    this.demoLoginRequired$.next(loginRequired);

    return !loginRequired;
  }

  /**
   * 用户信息初始化
   */
  public async userInfoInit(skipLoginRedirect = false): Promise<UserInfo> {
    const response = await this.basicInfoApi
      .getUserInfo(
        skipLoginRedirect
          ? {
              filter: false,
              showMessage: false,
              headers: { [SKIP_LOGIN_REDIRECT_HEADER]: 'true' },
            }
          : {}
      )
      .toPromise();
    const data = response?.data || ({} as UserInfo);
    this.userInfo$.next(data);

    if (!!(window as any).LwTrack) {
      // 埋点 初始化
      (window as any).lwTrackEvent = new (window as any).LwTrack({
        user_id: data?.id,
        distributor_id: data?.distributor_id || 0,
        // 环境变量
        environment: environment.eventName,
      });
      // 埋点 全局事件监听
      (window as any).lwTrackEvent.trackAddEventListener();
    }

    return data;
  }

  /**
   * 店铺信息初始化
   */
  public accountInfoInit() {
    this.basicInfoApi
      .getAccountInfo({
        params: {
          column: 'todayConsume',
          sort: 'desc',
        },
      })
      .subscribe(({ data }: ApiResponse<Create.AccountInfo>) => {
        this.accountInfo$.next(data?.list);
      });
  }

  /**
   * Saas用户信息初始化
   * 用于获取第三方公司域名的备案信息
   */
  public saasUserInfoInit() {
    if (this.document.location.hostname === 'optimize.linkworld.cc') {
      this.saasUserInfo$.next({});
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      this.basicInfoApi
        .getSaasUserInfo({
          params: {},
          error: () => {
            this.saasUserInfo$.next({});
            resolve(null);
          },
        })
        .subscribe(({ data }: ApiResponse) => {
          this.saasUserInfo$.next(data || {});
          resolve(null);
        });
    });
  }
}
